// src/utils/tesisat/buildTesisatMesh.js
//
// Redux tesisat item'indan THREE.js mesh ureten factory.
// Her tesisat tipi icin ayri builder fonksiyonu + dispatch eden buildMesh().
// Saf fonksiyonlar — React/Redux bagimliligi yok, sadece THREE.js + symbol factories.

import * as THREE from 'three';
import { createSingleSocket } from '../../../factory/symbols/createSocketSymbols';
import { createSupplyPoint } from '../../../factory/symbols/createSupplyPoint';
import { createMainPanel } from '../../../factory/symbols/createMainPanel';
import { createPanel } from '../../../factory/symbols/createPanel';
import { createCableShaftObject } from '../../../factory/symbols/createCableShaft';
import { createLightSwich } from '../../../factory/symbols/createLightingSymbols';
import { applySwitchHidden } from '../../../utils/switchHiddenState';
import { createLightFixture } from '../../../factory/symbols/createLightFixtureSymbols';
import { createMachineSymbol } from '../../../factory/symbols/createMachines';
import { resolveWorldPosition } from '../../../shared/coordinates/floorPlanOrigin';

// ── Per-type builders ────────────────────────────────────────────────────────

function buildSocket(item, pos) {
  const mesh = createSingleSocket(
    null, pos, item.dir ?? 1, item.color ?? 0x0045ff, false,
    item.subtype || 'normal', null, item.rotateValue || 0,
    item.id, item.stretchLength || 0,
  );
  if (!mesh) return null;
  mesh.userData = {
    ...mesh.userData,
    kind: 'socket', type: 'socket', isSelectable: true,
    originalColor: item.color ?? 0x0045ff,
    rotate: item.rotateValue || 0,
    floorPlanId: item.floorPlanId || null,
  };
  applyRotation(mesh, item.rotation);
  return mesh;
}

function buildSupplyPoint(item, pos) {
  const mesh = createSupplyPoint(null, pos, item.label || 'Supply', item.id);
  if (!mesh) return null;
  mesh.userData = { ...mesh.userData, id: item.id, floorPlanId: item.floorPlanId || null };
  applyRotation(mesh, item.rotation);
  return mesh;
}

function buildMainPanel(item, pos) {
  const mesh = createMainPanel(
    null, pos, false, item.id,
    item.panelInfo || { name: 'Panel' }, item.dir || 1,
  );
  if (!mesh) return null;
  mesh.userData = {
    ...mesh.userData, id: item.id,
    floorPlanId: item.floorPlanId || null,
    isPreview: false, isPanelPreview: false,
  };
  applyRotation(mesh, item.rotation);
  return mesh;
}

function buildElectricalPanel(item, pos) {
  const mesh = createPanel(
    null, pos, item.dir || 1, item.symmetricalOffset || 0,
    false, item.panelKatPlanNo || 0, item.panelInfo || { name: 'Panel' },
  );
  if (!mesh) return null;
  mesh.userData = {
    ...mesh.userData, id: item.id,
    floorPlanId: item.floorPlanId || null,
    isPreview: false, isPanelPreview: false,
  };
  mesh.traverse?.(child => {
    if (child.userData?.isPanelPart && child.userData.panelID) {
      child.userData.panelID = item.id;
    }
  });
  applyRotation(mesh, item.rotation);
  return mesh;
}

function buildCableShaft(item) {
  let c1 = item.corner1;
  let c2 = item.corner2;
  if (item.localCorner1 && item.localCorner2 && item.floorPlanId) {
    const w1 = resolveWorldPosition({ localPosition: item.localCorner1, floorPlanId: item.floorPlanId, position: item.corner1 });
    const w2 = resolveWorldPosition({ localPosition: item.localCorner2, floorPlanId: item.floorPlanId, position: item.corner2 });
    if (w1) c1 = w1;
    if (w2) c2 = w2;
  }
  if (!c1 || !c2) return null;
  return createCableShaftObject(c1, c2, 0x00ffff, {
    id: item.id, shaftGroupId: item.shaftGroupId,
    floorPlanId: item.floorPlanId, floorPlanName: item.floorPlanName,
  });
}

function buildLightSwitch(item, pos) {
  const color = item.color ?? item.originalColor ?? 0x0045ff;
  // Factory dir=sayisal: offsetGroup.rotation.z = rotateValue (derece→radyan).
  // applyRotation: group.rotation.z = item.rotation (direction acisi, radyan).
  // Ikisi farkli objelere uygulanir — cakismaz. rotateValue geri verilmeli.
  const mesh = createLightSwich(
    null, pos, item.dir ?? 1, color, false,
    item.subtype || 'normal', null, item.rotateValue || 0,
    item.id, item.stretchLength || 0,
  );
  if (!mesh) return null;
  mesh.userData = {
    ...mesh.userData, id: item.id,
    type: 'LightSwitch', kind: 'LightSwitch',
    isSelectable: true, floorPlanId: item.floorPlanId || null,
    originalColor: color,
  };
  applyRotation(mesh, item.rotation);
  // ✅ Gizli anahtar: reload'da soluk görünüm + export bayrağını yeniden uygula
  if (item.hidden) applySwitchHidden(mesh, true);
  return mesh;
}

function buildLightFixture(item, pos) {
  const color = item.color ?? item.originalColor ?? 0x0045ff;
  // Factory group.rotation.z = rotateValue + textGroup.rotation.z = -rotateValue
  // (counter-rotation). rotateValue verilmeli ki textGroup dogru acida olsun.
  // applyRotation ayni degeri group.rotation.z'ye yazar (fazlalik degil, overwrite).
  const mesh = createLightFixture(
    null, pos, item.dir ?? 1, color, null, false,
    item.symbol || 'normal', null, item.rotateValue || 0, item.id,
  );
  if (!mesh) return null;
  mesh.userData = {
    ...mesh.userData, id: item.id,
    type: 'LightFixture', isSelectable: true,
    floorPlanId: item.floorPlanId || null,
    fixtureType: item.fixtureType || mesh.userData.fixtureType,
    power: item.power || mesh.userData.power,
    lampPower: item.lampPower ?? mesh.userData.lampPower,
    lampCount: item.lampCount ?? mesh.userData.lampCount,
    description: item.description ?? mesh.userData.description,
    originalColor: color,
  };
  applyRotation(mesh, item.rotation);
  return mesh;
}

function buildMachine(item, pos) {
  const color = item.color ?? item.originalColor ?? 0xcc5600;
  // dir: serialize sonrasi plain object {x,y,z} olarak gelir — Vector3'e cevir.
  // angle: offsetGroup.rotation.z icin (group.rotation.z kullanilmiyor, cakisma yok).
  let dir = item.dir ?? 1;
  if (dir && typeof dir === 'object' && ('x' in dir) && ('y' in dir)) {
    dir = new THREE.Vector3(dir.x, dir.y, dir.z ?? 0);
  }
  const mesh = createMachineSymbol(
    null, pos, dir, color, false,
    item.machineType || 'Bulaşık Makinesi', item.power || '0',
    item.angle, item.id,
  );
  if (!mesh) return null;
  mesh.userData = {
    ...mesh.userData, id: item.id,
    type: 'machine', isSelectable: true,
    floorPlanId: item.floorPlanId || null,
    originalColor: color,
  };
  return mesh;
}

function buildBuat(item, pos) {
  const isSquare = item.buatType === 'square' ||
    item.type === 'buatSquare' || item.type === 'buatLightingSquare';
  const color = item.color ?? 0x0045ff;
  const mesh = isSquare
    ? new THREE.Mesh(new THREE.PlaneGeometry(5, 5), new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide }))
    : new THREE.Mesh(new THREE.CircleGeometry(2, 32), new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide }));
  mesh.position.set(pos.x, pos.y, 0.2);
  mesh.userData = {
    id: item.id, type: item.type,
    buatType: item.buatType || (isSquare ? 'square' : 'circle'),
    socketUUID: item.socketUUID || null,
    wireID: item.wireID || null,
    panelID: item.panelID || null,
    floorPlanId: item.floorPlanId || null,
    isSelectable: true, isDraggable: true,
  };
  // ✅ Gizli buat (tek anahtarı gizlenen lighting buat): reload'da soluk + export dışı
  if (item.hidden) applySwitchHidden(mesh, true);
  return mesh;
}

// ── Shared helper ────────────────────────────────────────────────────────────

function applyRotation(mesh, rotation) {
  if (typeof rotation === 'number' && rotation !== 0) {
    mesh.rotation.z = rotation;
  }
}

// ── Type dispatch map ────────────────────────────────────────────────────────

const BUAT_TYPES = new Set([
  'socketBuat', 'buatCircle', 'buatSquare',
  'lightingBuat', 'buatLightingCircle', 'buatLightingSquare',
]);

const TYPE_BUILDERS = {
  socket: buildSocket,
  supplyPoint: buildSupplyPoint,
  mainPanel: buildMainPanel,
  electricalPanel: buildElectricalPanel,
  cableShaft: buildCableShaft,
  LightSwitch: buildLightSwitch,
  LightFixture: buildLightFixture,
  machine: buildMachine,
};

// ── Main entry point ─────────────────────────────────────────────────────────

/**
 * Redux tesisat item'indan THREE.js mesh olustur.
 * @param {Object} item - tesisatSlice item (id, type, position, ...)
 * @returns {THREE.Object3D|null}
 */
export function buildTesisatMesh(item) {
  if (!item || !item.type) return null;

  const worldPos = resolveWorldPosition(item);
  if (!worldPos) return null;

  const pos = new THREE.Vector3(worldPos.x ?? 0, worldPos.y ?? 0, worldPos.z ?? 0);

  // cableShaft uses corners, not pos
  if (item.type === 'cableShaft') return buildCableShaft(item);

  // Buat types
  if (BUAT_TYPES.has(item.type)) return buildBuat(item, pos);

  // Standard types
  const builder = TYPE_BUILDERS[item.type];
  if (builder) return builder(item, pos);

  return null;
}
